import { type JanusCtx, type Row, activeStatuses, ecId, handBack, itemField, record, scalar, upper } from './context.js';
import { geometryEnvironment } from '../geometry/port-environment.js';
import { checkUuid } from '../pl/contract.js';
import { values } from '../port/k3-rules/access.js';
import { ftArcaneArrowDeliveryMatches } from '../port/k3-rules/arcane_fletcher.js';
import { ftSanguineCounterspellAgainstDisCarriers, ftSanguineCounterspellAgainstDisTotal } from '../port/k3-rules/sanguine_counterspell_dis.js';
import type { NextSpellCarrierDeps } from '../port/k3-rules/next_spell_numeric_carriers.js';
import { statusItemId } from '../statuses/expiry.js';
import { casterLayerProvider } from '../actions/cast-range-gate.js';
import { casterLayerProviders, ctxProviderReaders } from './caster-spell-layers.js';
import { layerListEntryUuid } from '../actions/spell-components.js';
import { ftSanguineRefundConsumedCarrier, type RefundDeps } from '../port/k3-rules/template_spell_roll.js';
import { adjustCharges } from './charges/adjust-charges.js';
import type { Arr } from '../port/k3-rules/access.js';

declare module './context.js' {
  interface JanusCtx {
    /** Engine-owned Arcane Arrow release context; a posted marker alone does not establish delivery. */
    arcaneArrowDelivery?: { spell_id: string; caster_id: string };
  }
}

export function arcaneArrowDeliveryMatches(ctx: JanusCtx, ability: Row, marker: unknown): boolean {
  const env = geometryEnvironment({ janus_trigger_in_progress: () => !!ctx.trigger, item_field: (row: unknown, key: string) => itemField(record(row), key) });
  env.globals.ft_arcane_arrow_delivery = ctx.arcaneArrowDelivery;
  return ftArcaneArrowDeliveryMatches(ability, marker, env);
}

/** Hydrate native status wrappers through the same held-definition readers as the spell template. */
function mysteriousEnvironment(ctx: JanusCtx) {
  return geometryEnvironment<NextSpellCarrierDeps>({
    check_uuid: checkUuid, janus_trigger_in_progress: () => !!ctx.trigger,
    creature_payload_statuses: caster => activeStatuses(ctx, caster as Row).map(status => ({ ...status,
      status_arr: ctx.read.definition(statusItemId(status)) ?? record(status.status_arr) })),
    layer_has: (row, key) => ctx.read.has(row as Row, key),
    layer_value: (row, key) => ctx.read.value(row as Row, key),
    item_field: (row, key) => itemField(record(row), key),
  });
}

export function mysteriousDis(ctx: JanusCtx, caster: Row): number {
  return ftSanguineCounterspellAgainstDisTotal(caster, mysteriousEnvironment(ctx));
}

export function mysteriousCarriers(ctx: JanusCtx, caster: Row): Row[] {
  const ids = new Set(values(ftSanguineCounterspellAgainstDisCarriers(caster, mysteriousEnvironment(ctx)))
    .map(carrier => upper(scalar(record(carrier)?.status_id))));
  return activeStatuses(ctx, caster).filter(status => ids.has(upper(scalar(status.status_id))));
}

/** The legacy protection needs an armed carrier; knowing the passive alone does not protect an ordinary spell. */
export function mysteriousUncounterable(ctx: JanusCtx, caster: Row): boolean | undefined {
  if (!mysteriousCarriers(ctx, caster).length) return false;
  const provider = casterLayerProvider(ctxProviderReaders(ctx), caster, 'sanguine_spells_cannot_be_counterspelled');
  return provider === undefined ? undefined : provider !== null;
}

/** Refund only the exact recorded debit. Retiring the carrier in the same truth command supplies its one-use identity. */
export function refundMysteriousCarrier(ctx: JanusCtx, caster: Row, status: Row, baseLevel: number): void {
  let storage: Row | null;
  try { storage = record(JSON.parse(String(scalar(status.status_casting_targets_arr) ?? ''))); } catch { return; }
  const receipt = record(storage?.sanguine_charge_receipt), sanguine = statusItemId(status), holder = ecId(caster);
  if (upper(scalar(status.status_caster_id)) !== holder || upper(scalar(status.status_target_id)) !== holder
      || receipt?.version !== 1 || upper(receipt.item_id) !== sanguine || receipt.refunded !== false) return;
  const debits = record(receipt.debits);
  if (!debits || Object.entries(debits).some(([pool, qty]) => !checkUuid(pool) || !Number.isSafeInteger(qty) || Number(qty) < 1)) return;
  const env = geometryEnvironment<RefundDeps>({
    find_caster_layer_provider_arrs: () => casterLayerProviders(ctxProviderReaders(ctx), caster, 'sanguine_free_for_sanguine')
      ?? handBack('sanguine-refund:provider-not-held'),
    layer_has: (row, key) => ctx.read.has(row as Row, key),
    layer_value: (row, key) => ctx.read.value(row as Row, key),
    layer_entries: (row, key) => ctx.read.entries(row as Row, key),
    ft_normalize_layer_entries_to_distinct_uuids: entries => [...new Set(values(entries).map(entry => upper(layerListEntryUuid(entry))))],
    ft_resolve_layer_entry: id => ctx.read.definition(upper(id)),
    item_field: (row, key) => itemField(record(row), key),
    ft_sanguine_claim_charge_receipt: () => debits,
    adjust_charges: (_caster, _affected, id, qty) => adjustCharges(ctx, caster, id, Number(qty), true),
  });
  ftSanguineRefundConsumedCarrier({ value: caster }, { value: [] },
    { status_id: upper(scalar(status.status_id)), status_item_id: sanguine }, baseLevel, '', { value: {} as Arr }, env);
}
